/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { HIGHCHARTS_DEFAULT_OPTIONS } from '../../../../Chart.constants';
import { LineChartProps } from '../../LineChart.props';

/**
 * Parse LineChart props into a Highcharts format for the general options.
 */
export const getLineChartGeneralOptions = (props: LineChartProps): Highcharts.Options => {
  const {
    axisXLabel,
    axisYLabel,
    description,
    height,
    labels,
    maxX,
    minX,
    showGrid,
    boldLines,
    headerPosition,
    tickMarks,
    hideTooltip,
    subtitle,
    displayUnitsOnAxis,
    title,
    unit,
    width,
  } = {
    ...props,
  };
  const options = { ...HIGHCHARTS_DEFAULT_OPTIONS };

  options.chart = { height, width };
  options.subtitle = {
    align: headerPosition || 'center',
    text: subtitle,
    useHTML: false,
  };
  options.title = { align: headerPosition || 'center', text: title, useHTML: false };
  options.tooltip = {
    enabled: !hideTooltip,
    shadow: {
      color: 'rgba(0,0,0,0.4)',
      offsetX: 0,
      offsetY: 0,
      width: 3,
    },
    useHTML: true,
  };
  options.xAxis = {
    categories: labels as Array<string>,
    gridLineWidth: showGrid ? 1 : 0,
    labels: {
      style: {
        fontWeight: boldLines ? 'bold' : 'normal',
      },
    },
    ...(minX && { min: minX }),
    ...(maxX && { max: maxX }),
    ...(tickMarks && { startOnTick: false, tickLength: 10, tickmarkPlacement: 'on', tickPosition: 'outside' }),
    ...(boldLines && {
      gridLineColor: '#ccc',
      lineColor: '#000',
      lineWidth: 1.5,
      tickColor: '#000',
      tickWidth: 1.5,
    }),
    title: { text: axisXLabel ?? '' },
  };
  options.yAxis = {
    ...options.yAxis,
    labels: {
      format: displayUnitsOnAxis ? '{value} ' + unit : '{value}',
      style: {
        fontWeight: boldLines ? 'bold' : 'normal',
      },
    },
    title: { text: axisYLabel ?? '' },
    ...(tickMarks && { tickLength: 10, tickPosition: 'outside', tickWidth: boldLines ? 1.5 : 1 }),
    ...(boldLines && {
      gridLineColor: '#ccc',
      lineColor: '#000',
      lineWidth: 1.5,
      tickColor: '#000',
    }),
  };

  if (description) {
    options.caption = {
      ...options.caption,
      align: 'center',
      text: description,
    };
  }

  if (unit) {
    options.tooltip = {
      ...options.tooltip,
      valueSuffix: ' ' + unit,
    };
  }

  return options;
};
